/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ChannelReference } from '../models/ChannelReference';
import type { ConnectionKey } from '../models/ConnectionKey';
import type { DeviceResponse } from '../models/DeviceResponse';
import type { HspState } from '../models/HspState';
import type { PauseOnStarving } from '../models/PauseOnStarving';
import type { PlaybackRate } from '../models/PlaybackRate';
import type { ServerTimeEstimate } from '../models/ServerTimeEstimate';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class StreamService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Setup the stream protocol.
     * Setup the stream protocol to play a stream.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-stream">Stream Protocol (STREAM)</a> for additional information about the STREAM protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param requestBody Stream setup
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Stream state.
     * @throws ApiError
     */
    public setupStream(
        xConnectionKey: (ConnectionKey | ChannelReference),
        requestBody: {
            /**
             * The stream reference of the stream to play.
             */
            stream_ref: string;
            /**
             * Enable HSP notifications for the stream playback. STREAM supresses HSP notifications by default since for most use cases they are not needed, since pushing data to the device is handled server side.
             */
            notify?: boolean;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/stream/setup',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Start the stream playback.
     * Start the stream playback.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-stream">Stream Protocol (STREAM)</a> for additional information about the STREAM protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param requestBody Stream play
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Stream state.
     * @throws ApiError
     */
    public streamPlay(
        xConnectionKey: (ConnectionKey | ChannelReference),
        requestBody: {
            /**
             * The start time in milliseconds.
             */
            start_time: number;
            server_time: ServerTimeEstimate;
            playback_rate?: PlaybackRate;
            /**
             * The loop state.
             */
            loop?: boolean;
            pause_on_starving?: PauseOnStarving;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/stream/play',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Stop the stream playback.
     * Stop the stream playback.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-stream">Stream Protocol (STREAM)</a> for additional information about the STREAM protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Stream state.
     * @throws ApiError
     */
    public streamStop(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/stream/stop',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Get the current stream state of the device.
     * Get the current stream state of the device.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-stream">Stream Protocol (STREAM)</a> for additional information about the STREAM protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Stream state.
     * @throws ApiError
     */
    public getStreamState(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/stream/state',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Adjust the stream synchronization.
     * Adjust the stream playtime using the provided current time sample from the external source and filter.
     *
     * This can improve the synchronization between the device and the external source when the current time samples have some variable inaccuracies.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-stream">Stream Protocol (STREAM)</a> for additional information about the STREAM protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param requestBody STREAM time
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HSP state.
     * @throws ApiError
     */
    public setStreamTime(
        xConnectionKey: (ConnectionKey | ChannelReference),
        requestBody: {
            /**
             * The time.
             */
            current_time: number;
            server_time: ServerTimeEstimate;
            /**
             * The filter to use when setting the time.
             */
            filter?: number;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/stream/synctime',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}
